import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Extract Document Content Step v4
 *
 * Single-document, document-first extraction step. Differences from v3:
 *  - Input is a single `CORE_DOCUMENT` entity ref — no `InboundEmail` coupling
 *    and no per-step batching/fanout.
 *  - Output carries the extracted payload directly (`extractedResult`) plus
 *    a scalar status. There is no `documents[]` / `documentOcrResults[]`
 *    aggregate and no processed/failed/skipped counts.
 *  - Persistence of the OCR result row is configurable via two independent
 *    flags so the same handler covers production writes, read-only reuse,
 *    and fully stateless extraction.
 *
 * Schema resolution is "stored schemas only" — V4 has two modes:
 *
 *  1. **Stored schema (pin)**: configure `extractionSchemaDefinitionId`
 *     (+ optional `extractionSchemaDefinitionVersionId`). One schema for
 *     every document the step sees. Enables typed projection sockets.
 *  2. **Auto**: set `useClassificationBasedSchema: true`. At runtime, V4
 *     looks up an active stored `SchemaDefinition` whose `documentType`
 *     matches the document's `classificationType`. If the document has no
 *     classification or no stored schema matches, the step returns
 *     `status: "skipped"`. Typed projection sockets are unavailable.
 *
 * There is no hard-coded built-in Extend AI schema fallback — workflows
 * that want a specific schema must reference a stored one.
 *
 * Dynamic projection sockets retain the v3 schema-version contract but are
 * scalar (one value per socket) since v4 processes exactly one document:
 *   - `per_field` (default): one socket per declared field carrying the value
 *   - `single_object`: a single `extractedFields: json` socket carrying the
 *     whole projected object
 *
 * The base outputs are declared on the static `outputSchema` below. Dynamic
 * projection sockets are computed on the editor side from the loaded
 * snapshot and persisted on save (same mirror pattern as v3).
 */
export const extractDocumentContentV4Step = defineStepConfig({
  type: "extract_document_content_v4",
  name: "Extract Document Content (V4)",
  description:
    "Run OCR + extraction on a single document and emit a scalar extracted payload with optional schema-driven projection sockets",
  category: "AI & Agents",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The document whose content should be extracted",
    ),
  }),

  // `.catchall(z.unknown())` so dynamic projection keys (`field:<name>` or
  // `extractedFields`) survive Zod's parse step — `z.object({...})` would
  // strip unknown keys and silently drop every typed projection socket
  // payload (same trick v3 uses).
  outputSchema: z
    .object({
      status: z
        .enum(["completed", "failed", "skipped"])
        .describe("Per-document outcome status"),
      extractedResult: z
        .record(z.string(), z.unknown())
        .optional()
        .describe(
          "Extracted payload returned by the OCR/extraction provider, when available",
        ),
      ocrResult: entityOfType(SystemEntity.CORE_DOCUMENT_OCR_RESULT)
        .optional()
        .describe(
          "Reference to the persisted OCR result, when persistOcrResult is enabled",
        ),
      error: z
        .string()
        .optional()
        .describe("Error message when status is 'failed'"),
      persisted: z
        .boolean()
        .describe("True when a new OCR result row was written for this run"),
      reusedExistingResult: z
        .boolean()
        .describe(
          "True when an existing completed OCR result row was reused instead of calling the provider",
        ),
      skipReason: z
        .string()
        .optional()
        .describe(
          "Human-readable reason when status is 'skipped' (no classification, no matching stored schema, unsupported file, etc.)",
        ),
    })
    .catchall(z.unknown()),

  configSchema: z.object({
    extractionSchemaDefinitionId: z
      .uuid()
      .optional()
      .describe(
        "Stored extraction schema definition to compile and use for every document the step sees",
      ),
    extractionSchemaDefinitionVersionId: z
      .uuid()
      .optional()
      .describe(
        "Pinned version of the schema definition. The version's immutable snapshot drives both extraction and typed projection sockets. Omit to track the active version.",
      ),
    useClassificationBasedSchema: z
      .boolean()
      .default(true)
      .describe(
        "Default mode for v4. When true, resolve the extraction schema at runtime from the document's classification (matching active stored schema). When no schema matches, the step returns status: 'skipped'. Typed projection sockets are unavailable in this mode. Set to false (and pin a stored schema) to opt into deterministic per-step schema selection.",
      ),
    projectionOutputMode: z
      .enum(["per_field", "single_object"])
      .optional()
      .describe(
        'Output shape: "per_field" emits one dynamic socket per declared field (default); "single_object" emits one extractedFields socket carrying the whole payload. Ignored in Auto mode.',
      ),
    persistOcrResult: z
      .boolean()
      .optional()
      .describe(
        "When true (default), persist a CORE_DOCUMENT_OCR_RESULT row after a fresh extraction call. Set to false for stateless / dry-run executions.",
      ),
    reuseExistingOcrResult: z
      .boolean()
      .optional()
      .describe(
        "When true (default), look up a matching completed OCR result row before calling the provider. Set to false to force a fresh extraction every run.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
    nonRetryableErrorTypes: [
      "InvalidFileFormatError",
      "UnsupportedMimeTypeError",
    ],
  },

  // Surfaces the Evals tab in the step editor. The evaluator
  // (ExtractDocumentContentV4Evaluator) reads `extractedResult` directly
  // off the step output — payload is inline whether the run was fresh or
  // reused a prior OCR result row, so no DB hydration is required.
  evalConfig: {
    evalOutputKeys: ["extractedResult"],
    outputFormat: "object",
  },
});
